import { Subscription, Observable } from 'rxjs';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { FormFieldConfig } from '@shared/interfaces/config';
import { ValidationObserver } from 'vee-validate';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { ApiRouteType } from '@core/interfaces/core-services';

@Component({
  name: 'bpc-vuex-form-provider',
  components: {
    'validation-observer': ValidationObserver,
  },
})
export default class VuexFormProvider extends Vue {
  @Prop(String) readonly entity: string;
  @Prop(String) readonly storeName: string;
  @Prop([String, Number]) readonly entityId: string | number;
  @Prop(String) readonly savePath: string;
  @Prop(String) readonly searchPath: string;
  @Prop(String) readonly configPath: string;
  @Prop(Boolean) readonly fetchEntity: boolean;
  @Prop(String) readonly fetchPath: string;
  @Prop(String) readonly mode: string;

  @Prop({ type: String, default: 'default' }) readonly formConfigName: string;
  @Prop(Object) readonly value: AnyEntity;

  // This field is used to save the filters values of field to manage in backend if necessary to relate the entity model with other.
  @Prop([String, Object, Array]) readonly extraData: string | AnyEntity;

  @Prop({ type: String, default: 'global.generic.successful_action' })
  readonly createdSuccessText: string;
  @Prop({ type: String, default: 'global.generic.successful_action' })
  readonly updatedSuccessText: string;
  @Prop({ type: String, default: 'global.generic.error_form_action' })
  readonly errorMessageText: string;
  @Prop({ type: Boolean, default: false }) readonly withDraft: boolean;
  @Prop({ type: String, default: null }) readonly apiRouteType: ApiRouteType;

  state = null;
  errorsObject: object = {};
  searchEntitySubscription: Subscription;
  searchEntityObservable: Observable<BPCGenericApiResponse>;
  saveEntitySubscription: Subscription;
  saveEntityObservable: Observable<BPCGenericApiResponse>;
  deleteMediaFileObservable: Observable<BPCGenericApiResponse>;

  public get fetchEntityPath() {
    if (this.fetchPath) {
      return this.fetchPath;
    } else if (this.entityId) {
      return `${this.$bpcApiRouteHelper.getEntityPath(this.entity, 'raw')}/${this.entityId}`;
    }

    return this.$bpcApiRouteHelper.getEntityPath(this.entity, 'raw');
  }

  public get saveEntityPath(): string {
    if (this.savePath) {
      return this.savePath;
    } else if (this.entityId) {
      return `${this.$bpcApiRouteHelper.getEntityPath(this.entity)}/${this.entityId}`;
    }

    return this.$bpcApiRouteHelper.getEntityPath(this.entity);
  }

  public get searchEntityPath(): string {
    if (this.searchPath) {
      return this.searchPath;
    }
    return `${this.$bpcApiRouteHelper.getSearchBookingPath(this.entity)}/search`;
  }

  public get entityFormConfigSlug(): string {
    return this.configPath ? this.configPath : `${this.entity}.forms.${this.formConfigName}`;
  }

  public get model(): AnyEntity {
    return this.$store.getters[`${this.storeName}/model`];
  }

  public set model(val: AnyEntity) {
    this.$emit('input', val);
    this.$store.commit(`${this.storeName}/setModel`, val);
  }

  public get rawModel(): AnyEntity {
    return this.$store.getters[`${this.storeName}/rawModel`];
  }

  public set rawModel(val: AnyEntity) {
    this.$store.commit(`${this.storeName}/setRawModel`, val);
  }

  public get initialized(): boolean {
    return this.$store.getters[`${this.storeName}/initialized`];
  }

  public get loading(): boolean {
    return this.$store.getters[`${this.storeName}/isLoading`];
  }

  public get loadingConfig(): boolean {
    return this.$store.getters[`${this.storeName}/flags`].loadingConfig;
  }

  public get saving(): boolean {
    return this.$store.getters[`${this.storeName}/flags`].saving;
  }

  public get search(): boolean {
    return this.$store.getters[`${this.storeName}/flags`].searching;
  }

  public get fieldsObject(): Record<string, FormFieldConfig> {
    return this.$store.getters[`${this.storeName}/fieldsObject`];
  }

  public get hasMedia(): boolean {
    return this.$store.getters[`${this.storeName}/configuration`].hasMedia;
  }

  public get disableTag(): boolean {
    return this.$store.getters[`${this.storeName}/configuration`].disableTag;
  }
  public get disabled(): boolean {
    return this.$store.getters[`${this.storeName}/configuration`].disabled;
  }

  public get groupByTabs(): boolean {
    return this.$store.getters[`${this.storeName}/configuration`].groupByTabs;
  }

  public get tabs(): object {
    return this.$store.getters[`${this.storeName}/configuration`].tabs;
  }

  public get lazyTabs(): object {
    return this.$store.getters[`${this.storeName}/configuration`].lazyTabs;
  }

  public get fields(): FormFieldConfig[] {
    return this.fieldsObject ? Object.values(this.fieldsObject).sort(this.compareByOrder) : [];
  }

  public get apiMode(): 'api' | 'storage' {
    return this.$store.getters[`${this.storeName}/mode`] || 'api';
  }

  public get useWithDraft(): boolean {
    return this.$store.getters[`${this.storeName}/withDraft`] || false;
  }

  public compareByOrder(a: FormFieldConfig, b: FormFieldConfig): number {
    if (a.order < b.order) {
      return -1;
    }
    if (a.order > b.order) {
      return 1;
    }
    return 0;
  }

  public async mounted(): Promise<void> {
    console.log('vuex-form-provider created');
    this.$store.commit(`${this.storeName}/setMode`, this.mode);
    this.$store.commit(`${this.storeName}/setWithDraft`, this.withDraft);
    this.$store.commit(`${this.storeName}/setExtraData`, this.extraData);
    await this.$store.dispatch(`${this.storeName}/initializeFormModel`, {
      entity: this.entity,
      fetchEntity: this.fetchEntity,
      id: this.entityId,
      formName: this.formConfigName,
      configPath: this.configPath,
      fetchPath: this.fetchPath,
      forceUpdateConfig: true,
      requestService: this.getBPCRequestService(),
    });

    this.$emit('model-loaded', this.model);
  }

  // public created(): void {

  // }

  public async saveEntity(entity: AnyEntity): Promise<BPCGenericApiResponse> {
    try {
      const res: BPCGenericApiResponse = await this.$store.dispatch(
        `${this.storeName}/saveEntity`,
        {
          entity: entity,
          entityId: this.entityId,
          hasMedia: this.hasMedia,
          requestService: this.getBPCRequestService(),
          apiRouteType: this.apiRouteType,
          withDraft: this.useWithDraft,
          saveEntityPath: this.saveEntityPath,
        },
      );
      const successText = this.entityId ? this.updatedSuccessText : this.createdSuccessText;
      this.$bpcMessage.showSuccessMessage({ text: successText });
      this.$emit('entity-saved', res);
      this.$emit('saving-entity', false);
      return res;
    } catch (error) {
      // There are some errors

      this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
      this.resetErrorObject();
      this.manageSaveErrors(error);
      this.$emit('saving-entity', false);
      throw error;
    }
  }

  public async searchEntity(entity: AnyEntity): Promise<BPCGenericApiResponse> {
    try {
      const res: BPCGenericApiResponse = await this.$store.dispatch(
        `${this.storeName}/searchEntity`,
        {
          entity: entity,
          requestService: this.getBPCRequestService(),
          searchEntityPath: this.searchEntityPath,
        },
      );

      // this.$bpcMessage.showSuccessMessage({ text: this.createdSuccessText });
      this.$emit('entity-searched', res);
      this.$emit('saving-entity', false); // TODO check if this is necesary

      return res;
    } catch (error) {
      // There are some errors

      this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
      this.resetErrorObject();
      this.manageSaveErrors(error);
      this.$emit('saving-entity', false); // TODO check if this is necesary
      throw error;
    }
  }

  public restartState(field) {
    if (this.errorsObject[field]) {
      this.$set(this.errorsObject[field], 'error', null);
      this.$set(this.errorsObject[field], 'state', false);
      this.$set(this.errorsObject[field], 'veeValidation', false);
    }
  }

  manageSaveErrors(error) {
    console.log(error.response.data);
    if (error.response.status === 422) {
      if (error.response.data.errors) {
        // this.errorsObject = error.response.data.errors;
        Object.keys(error.response.data.errors).forEach(field => {
          const errorField = {
            error: error.response.data.errors[field][0],
            state: false,
            veeValidation: false,
          };
          this.$set(this.errorsObject, field, errorField);
          // if (this.fieldsObject[field]) {
          //   this.$set(this.fieldsObject[field], 'error', error.response.data.errors[field][0]);
          //   this.$set(this.fieldsObject[field], 'state', false);
          //   this.$set(this.fieldsObject[field], 'veeValidation', false);
          // }
        });
      }
    }
  }

  private getBPCRequestService() {
    if (this.apiMode === 'storage') {
      return this.$bpcStorage;
    } else {
      return this.$bpcApi;
    }
  }

  resetErrorObject() {
    this.errorsObject = {};
  }

  public getFieldsByTag(tag: string, tab: string = 'default') {
    return this.fields
      .filter((field: FormFieldConfig) => {
        return this.groupByTabs
          ? field.tags.includes(tag) && field.tab === tab
          : field.tags.includes(tag);
      })
      .sort(this.compareByOrder);
  }

  public getFieldsWithAllTags(tags: string[], tab: string = 'default') {
    return this.fields
      .filter((field: FormFieldConfig) => {
        let res = true;
        tags.forEach(tag => {
          if (!field.tags.includes(tag)) {
            res = false;
          }
        });
        if (this.groupByTabs && field.tab !== tab) {
          res = false;
        }
        return res;
      })
      .sort(this.compareByOrder);
  }

  public getFieldsWithAnyTag(tags: string[], tab: string = 'default') {
    return this.fields
      .filter((field: FormFieldConfig) => {
        const tagsInIncludeArray = field.tags.filter(tag => {
          return tags.includes(tag);
        });
        if (this.groupByTabs && field.tab !== tab) {
          return false;
        } else {
          return !!tagsInIncludeArray.length;
        }
      })
      .sort(this.compareByOrder);
  }

  public getFieldsWithoutTags(tags: string[] | null = null, tab: string = 'default') {
    if (!tags) {
      return this.fields
        .filter((field: FormFieldConfig) => {
          return this.groupByTabs
            ? field.tab === tab && (!field.tags || !field.tags.length)
            : !field.tags || !field.tags.length;
        })
        .sort(this.compareByOrder);
    } else {
      return this.fields
        .filter((field: FormFieldConfig) => {
          const tagsInExcludeArray = field.tags.filter(tag => {
            return tags.includes(tag);
          });

          if (this.groupByTabs && field.tab !== tab) {
            return false;
          } else {
            return !tagsInExcludeArray.length;
          }
        })
        .sort(this.compareByOrder);
    }
  }
}
